import { useGT, useLocale } from 'gt-react';
import type { WorkbookDefinition, WorkbookRun } from '@erp/shared-types';

/** Inline, as "Sep 24, 3:41 PM". */
const SHORT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};
/** In a tooltip, as "Sep 24, 2026, 3:41:07 PM EDT". */
const FULL: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
  timeStyle: 'long',
};

export type WorkbookRunTime = ReturnType<typeof useWorkbookRunTime>;

/**
 * How the Workbook page dates a Run: a short time inline, and a tooltip
 * sentence with the full time, Run mode and who ran it.
 */
export function useWorkbookRunTime(
  definition: Pick<WorkbookDefinition, 'sheets' | 'outputs'>,
) {
  const gt = useGT();
  const locale = useLocale();
  const format = (at: string, options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, options).format(new Date(at));
  function mode(run: WorkbookRun) {
    if (run.mode === 'entire')
      return gt('Entire workbook', { context: 'workbook run mode summary' });
    if (run.mode === 'sample')
      return gt('On a sample', { context: 'workbook run mode summary' });
    const name =
      [...definition.sheets, ...definition.outputs].find(
        (item) => item.id === run.targetId,
      )?.name ??
      gt('a removed worksheet', { context: 'workbook run mode summary' });
    if (run.mode === 'up-to')
      return gt('Up to {name}', { context: 'workbook run mode summary', name });
    return run.mode === 'only'
      ? gt('Only {name}', { context: 'workbook run mode summary', name })
      : gt('From {name}', { context: 'workbook run mode summary', name });
  }
  return {
    /** The Run mode and its Target, as "Up to Billing Summary". */
    mode,
    short: (at: string) => format(at, SHORT),
    full: (at: string) => format(at, FULL),
    /** "Sep 24, 2026, 3:41:07 PM EDT · Entire workbook · Run by Ada". */
    describe: (run: WorkbookRun) =>
      [
        format(run.createdAt, FULL),
        mode(run),
        run.triggeredBy
          ? gt('Run by {name}', {
              context: 'workbook run summary',
              name: run.triggeredBy.name,
            })
          : gt('User unavailable', { context: 'workbook run summary' }),
      ].join(' · '),
  };
}
